import { create } from "zustand";

import type { Call, CallMode, CallPhase, CallStatus, DeviceCallState } from "./state";
import {
  BRIDGE,
  furthest,
  isTerminal,
  phaseFromDevice,
  phaseFromServer,
  resolvePhase,
} from "./state";

export type ActiveCall = {
  ref: string;
  mode: CallMode;
  /** What the agent dialled, for the bar. Not looked up — shown as typed. */
  dialedNumber: string;
  /** Who they are calling, when the call came from a contact rather than a keypad. */
  label: string;
  /**
   * Dialled from *Llamar*, to a number nobody has on file. What makes the
   * wrap-up offer to register the company it turned out to be.
   */
  keypad: boolean;
  serverStatus: CallStatus | null;
  deviceState: DeviceCallState;
  phase: CallPhase;
  /**
   * Wall clock at the client's first ring, which is where the timer starts —
   * or at the answer, for a call whose ring was never reported.
   */
  ringingAt: number | null;
  /** The server's seconds since that ring, once the call is over. Beats our timer. */
  duration: number | null;
  muted: boolean;
  /** The tones keyed in so far — an extension, a menu option — in order. */
  digits: string;
};

type SoftphoneState = {
  call: ActiveCall | null;
  /** Device constructed and microphone granted. Needs a user gesture first. */
  isDeviceReady: boolean;
  /** Anything the agent has to be told about, in Spanish, or null. */
  error: string | null;

  begin: (call: {
    ref: string;
    mode: CallMode;
    dialedNumber: string;
    label: string;
    status: CallStatus;
    keypad?: boolean;
  }) => void;
  applyServer: (ref: string, status: CallStatus, duration?: number | null) => void;
  applyDevice: (state: DeviceCallState) => void;
  setMuted: (muted: boolean) => void;
  /** Remember tones that were just sent, so the agent sees what they pressed. */
  noteDigits: (digits: string) => void;
  setDeviceReady: (ready: boolean) => void;
  setError: (error: string | null) => void;
  /** Clear the bar. Only ever called by the agent dismissing a finished call. */
  clear: () => void;
};

function recompute(call: ActiveCall): ActiveCall {
  const next = resolvePhase(
    phaseFromServer(call.serverStatus),
    phaseFromDevice(call.deviceState),
    call.mode,
  );
  const phase = furthest(call.phase, next);
  return {
    ...call,
    phase,
    ringingAt:
      call.ringingAt ??
      (phase === "ringing" || phase === "in_progress" ? Date.now() : null),
  };
}

/**
 * The active call, outside React.
 *
 * Zustand rather than context for the same reason as `realtime/store`: this
 * changes several times a second while a call runs (the timer alone), and a
 * context would re-render the whole app tree underneath it. The provider owns
 * the *device*; this owns what is on screen.
 */
export const useSoftphoneStore = create<SoftphoneState>((set) => ({
  call: null,
  isDeviceReady: false,
  error: null,

  begin: ({ ref, mode, dialedNumber, label, status, keypad = false }) =>
    set({
      error: null,
      call: recompute({
        ref,
        mode,
        dialedNumber,
        label,
        keypad,
        serverStatus: status,
        deviceState: "idle",
        phase: "idle",
        ringingAt: null,
        duration: null,
        muted: false,
        digits: "",
      }),
    }),

  applyServer: (ref, status, duration) =>
    set((state) => {
      // An event for a call that is not the one on screen. Two tabs, or the
      // tail of a call the agent already dismissed. Neither is an error, and
      // neither may overwrite the current bar.
      if (!state.call || state.call.ref !== ref) return state;
      return {
        call: recompute({
          ...state.call,
          serverStatus: status,
          duration: duration ?? state.call.duration,
        }),
      };
    }),

  applyDevice: (deviceState) =>
    set((state) => {
      if (!state.call || state.call.mode === BRIDGE) return state;
      return { call: recompute({ ...state.call, deviceState }) };
    }),

  setMuted: (muted) =>
    set((state) => (state.call ? { call: { ...state.call, muted } } : state)),

  noteDigits: (digits) =>
    set((state) =>
      state.call
        ? { call: { ...state.call, digits: state.call.digits + digits } }
        : state,
    ),

  setDeviceReady: (isDeviceReady) => set({ isDeviceReady }),
  setError: (error) => set({ error }),
  clear: () => set({ call: null }),
}));

/**
 * Whether a key pressed now would be heard on the other end.
 *
 * Only once somebody — or something, a switchboard — has answered: before that
 * there is nobody to hear a tone. And only on the softphone: in bridge mode the
 * audio is on the agent's own handset, and its keypad is the one that works.
 */
export function canSendTones(call: ActiveCall | null): call is ActiveCall {
  return call !== null && call.mode !== BRIDGE && call.phase === "in_progress";
}

/** Whether a call is live enough that closing the tab would drop it. */
export function isCallLive(call: ActiveCall | null): boolean {
  return call !== null && !isTerminal(call.phase);
}

/**
 * Whether the bar holds the summary of a call the client answered, which only
 * saving it puts away (`CallWrapUp`). Leaving the page would throw it away:
 * the bar lives in memory, and nothing brings it back on the next load.
 *
 * `ended` is a `completed` from the server, which Twilio reports only for a
 * client leg that was picked up; every way of not connecting is `failed`.
 */
export function awaitsSummary(call: ActiveCall | null): boolean {
  return call !== null && call.phase === "ended";
}
